import { analytics } from 'analytics';
import {
  convertToMaticInput,
  sankeyFormFlowsMapToArray,
  sankeyFormNodesMapToArray,
} from 'utils/sankey';

import { SankeyRenderState } from '../contexts/sankeyRenderContext';
import { buildRenderConfig } from './config';
import d3Sankey from './d3Sankey';
import JSInputParser from './jsInputParser';
import { getLabelPaddings } from './labelPaddings';
import NodeMoves from './nodeMoves';
import { fillInNodeColorsAndLabels } from './nodes';
import { setUpDiagramSize } from './size';

/*
 * Central function for building the Sankey diagram props used for rendering the diagram.
 */
export function buildSankeyDiagram(sankeyForm: SankeyForm): SankeyRenderState {
  // Build the JS input string from the form and then parse it back into actual nodes and flows.
  // TODO: Avoid double parsing here (it's also done in the DataEditor component).
  const formValues = sankeyForm.getValues();
  const jsInput = convertToMaticInput(
    sankeyFormFlowsMapToArray(formValues.flows),
    sankeyFormNodesMapToArray(formValues.nodes)
  );
  const { nodes, flows, cycles, maxDecimalPlaces } = new JSInputParser(jsInput)
    .withPreviousNodes(sankeyFormNodesMapToArray(formValues.nodes))
    .parse((sourceLine) => {
      analytics.reportError('Diagram parsing error', { sourceLine }, false);
    });

  // Central configuration object for how the rendered Sankey diagram should look like.
  const cfg = buildRenderConfig(formValues, maxDecimalPlaces);

  // Optimally position nodes and flows in the diagram.
  // NODE: Call to setup() is a mutable operation.
  const diagram = d3Sankey()
    .rightJustifyEndpoints(false)
    .leftJustifyOrigins(false)
    .withNodes(nodes)
    // Filter out back flows for the initial layout to set node positions
    // nicely and then add them back in for the final layout where we stick
    // to the same node positions to not mess up diagram with cyclic layout.
    .withFlows(flows.filter((f) => !f.isBackFlow))
    .setup({ updateStages: true })
    .withFlows(flows)
    .setup({ updateStages: false });

  // Get the highest node amount for percentage calculations
  const highestNodeAmount = Math.max(...nodes.map((item) => item.amount));

  // Fill in node colors and labels info
  fillInNodeColorsAndLabels(cfg, nodes, highestNodeAmount);

  // Get label paddings for the diagram
  const labelPaddings = getLabelPaddings(cfg);

  // Compute the final size of the graph
  const graph = setUpDiagramSize(cfg, labelPaddings, diagram.getStages());

  // Relayout the diagram based on the final graph size and the labels information
  diagram
    .size({ w: graph.w, h: graph.h })
    .nodeWidth(cfg.node.width)
    .nodeHeightFactor(cfg.node.height / 100)
    .nodeSpacingFactor(cfg.node.spacing / 100)
    .layout(25);

  // We update the final stages array here, because in theory they may
  // have been changed. The final array is then used to determine the
  // midpoint of the diagram which is helpful for label positioning.
  const stagesMidpoint = (diagram.getStages().length - 1) / 2;

  // Build the node moves object and apply it to all the nodes
  const nodeMoves = new NodeMoves(sankeyForm).applyAndCleanUp(nodes, graph);

  // Relayout the flows based on the new node positions
  diagram.relayoutFlows();

  return {
    isReady: true,
    cfg,
    graph,
    flows,
    nodes,
    cycles,
    highestNodeAmount,
    stagesMidpoint,
    labelPaddings,
    moveNodeTo: (node, move) => {
      nodeMoves.moveNodeTo(node, move, graph);
      // Relayout the flows as the node is being dragged
      diagram.relayoutFlows();
    },
    saveNodeMove: (node) => {
      nodeMoves.saveNodeMove(node);
    },
  };
}
